import type { MetadataGroup, MetadataRelationship, MetadataSettings, MetadataTable, ProjectMetadataV1 } from './schema'

export const METADATA_SCHEMA_VERSION_DRAFT = '2.0.0' as const

export interface MetadataV2Table extends Omit<MetadataTable, 'cols' | 'indexes'> {
  cols: Record<string, ProjectMetadataV1['tables'][number]['cols'][number]>
  colOrder: string[]
  indexes: Record<string, NonNullable<ProjectMetadataV1['tables'][number]['indexes']>[number]>
  indexOrder: string[]
}

export interface MetadataV2 {
  schemaVersion: typeof METADATA_SCHEMA_VERSION_DRAFT
  project: ProjectMetadataV1['project']
  tables: Record<string, MetadataV2Table>
  tableOrder: string[]
  relationships: Record<string, MetadataRelationship>
  relationshipOrder: string[]
  groups: Record<string, MetadataGroup>
  groupOrder: string[]
  layout: ProjectMetadataV1['layout']
  settings: Omit<MetadataSettings, 'userView'>
}

function stripUserView(settings: ProjectMetadataV1['settings'] | undefined): MetadataV2['settings'] {
  return {
    defaults: settings?.defaults ?? { prefix: [], suffix: [] },
    defaultLineMode: settings?.defaultLineMode ?? 'orthogonal',
    relAnimation: settings?.relAnimation ?? true,
    grid: settings?.grid ?? true,
  }
}

export function emptyMetadataV2(
  project: ProjectMetadataV1['project'] = { name: '', description: '', dialect: 'postgresql' },
): MetadataV2 {
  return {
    schemaVersion: METADATA_SCHEMA_VERSION_DRAFT,
    project,
    tables: {},
    tableOrder: [],
    relationships: {},
    relationshipOrder: [],
    groups: {},
    groupOrder: [],
    layout: { positions: {}, zoom: 1, pan: { x: 0, y: 0 } },
    settings: stripUserView(undefined),
  }
}

export function arrayToKeyed(v1: ProjectMetadataV1): MetadataV2 {
  return {
    schemaVersion: METADATA_SCHEMA_VERSION_DRAFT,
    project: v1.project,
    tables: Object.fromEntries(
      (v1.tables ?? []).map((table) => [
        table.id,
        {
          ...table,
          cols: Object.fromEntries((table.cols ?? []).map((col) => [col.id, col])),
          colOrder: (table.cols ?? []).map((col) => col.id),
          indexes: Object.fromEntries((table.indexes ?? []).map((index) => [index.id, index])),
          indexOrder: (table.indexes ?? []).map((index) => index.id),
        },
      ]),
    ),
    tableOrder: (v1.tables ?? []).map((table) => table.id),
    relationships: Object.fromEntries((v1.relationships ?? []).map((rel) => [rel.id, rel])),
    relationshipOrder: (v1.relationships ?? []).map((rel) => rel.id),
    groups: Object.fromEntries((v1.groups ?? []).map((group) => [group.id, group])),
    groupOrder: (v1.groups ?? []).map((group) => group.id),
    layout: v1.layout,
    settings: stripUserView(v1.settings),
  }
}

export function keyedToArray(v2: MetadataV2): ProjectMetadataV1 {
  return {
    schemaVersion: '1.1.0',
    project: v2.project,
    tables: v2.tableOrder
      .map((tableId) => v2.tables[tableId])
      .filter(Boolean)
      .map((table) => ({
        ...table,
        cols: table.colOrder.map((columnId) => table.cols[columnId]).filter(Boolean),
        indexes: table.indexOrder.map((indexId) => table.indexes[indexId]).filter(Boolean),
      })),
    relationships: v2.relationshipOrder.map((relId) => v2.relationships[relId]).filter(Boolean),
    groups: v2.groupOrder.map((groupId) => v2.groups[groupId]).filter(Boolean),
    layout: v2.layout,
    settings: stripUserView(v2.settings),
  }
}
